import { ActionButton } from './ActionButton';
import { StatusBadge } from './StatusBadge';

interface CampaignRow {
  id: string;
  nome: string;
  channels?: Array<'WHATSAPP' | 'EMAIL'>;
  sessionNames: string[];
  status: string;
  totalContacts: number;
  sentCount: number;
  _count?: {
    messages: number;
  };
}

interface CampaignTableProps {
  campaigns: CampaignRow[];
  onView: (campaignId: string) => void;
  onPause: (campaignId: string) => void;
  onResume: (campaignId: string) => void;
  onDelete: (campaignId: string) => void;
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M1.75 10S4.75 4.5 10 4.5 18.25 10 18.25 10 15.25 15.5 10 15.5 1.75 10 1.75 10Z" />
      <circle cx="10" cy="10" r="2.5" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor">
      <path d="M6 4.75A1.25 1.25 0 0 1 7.25 6v8A1.25 1.25 0 0 1 6 15.25 1.25 1.25 0 0 1 4.75 14V6A1.25 1.25 0 0 1 6 4.75Zm8 0A1.25 1.25 0 0 1 15.25 6v8A1.25 1.25 0 0 1 14 15.25 1.25 1.25 0 0 1 12.75 14V6A1.25 1.25 0 0 1 14 4.75Z" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor">
      <path d="M6.8 4.92c0-.96 1.05-1.56 1.88-1.05l7.21 4.57c.78.49.78 1.62 0 2.11L8.68 15.1c-.83.52-1.88-.08-1.88-1.04V4.92Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 4.75h5m-8 1.5h11m-1.25 0-.63 8.22A1.5 1.5 0 0 1 12.12 16H7.88a1.5 1.5 0 0 1-1.5-1.53l-.63-8.22m3 2.5v4.5m4-4.5v4.5" />
    </svg>
  );
}

export function CampaignTable({
  campaigns,
  onView,
  onPause,
  onResume,
  onDelete
}: CampaignTableProps) {
  return (
    <>
      <div className="hidden xl:block">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-slate-50/80">
              <tr className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                <th className="px-6 py-4">Campanha</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Contatos</th>
                <th className="px-6 py-4">Enviados</th>
                <th className="px-6 py-4">Sessões</th>
                <th className="px-6 py-4 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {campaigns.map((campaign) => {
                const channels = campaign.channels?.length
                  ? campaign.channels
                  : (campaign.sessionNames?.length ? ['WHATSAPP'] : ['EMAIL']);
                const totalDeliveries = campaign._count?.messages ?? campaign.totalContacts;

                return (
                  <tr key={campaign.id} className="transition-colors duration-200 hover:bg-slate-50/80">
                    <td className="px-6 py-5 align-top">
                      <div className="space-y-1">
                        <div className="text-sm font-semibold text-slate-900">{campaign.nome}</div>
                        <div className="text-xs text-slate-500">
                          {channels.length === 2
                            ? 'Por Email e WhatsApp'
                            : channels[0] === 'EMAIL'
                              ? 'Por Email'
                              : 'Por WhatsApp'}
                        </div>
                        <div className="flex flex-wrap gap-2 pt-1">
                          {channels.map((channel) => (
                            <span
                              key={channel}
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                channel === 'EMAIL'
                                  ? 'bg-violet-50 text-violet-700 ring-1 ring-violet-200'
                                  : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                              }`}
                            >
                              {channel === 'EMAIL' ? 'Email' : 'WhatsApp'}
                            </span>
                          ))}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      <StatusBadge status={campaign.status} />
                    </td>
                    <td className="px-6 py-5 align-top text-sm font-medium text-slate-700">{campaign.totalContacts}</td>
                    <td className="px-6 py-5 align-top">
                      <div className="text-sm font-semibold text-slate-900">{campaign.sentCount}</div>
                      <div className="text-xs text-slate-500">de {totalDeliveries} disparos</div>
                    </td>
                    <td className="px-6 py-5 align-top">
                      {campaign.sessionNames?.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {campaign.sessionNames.slice(0, 3).map((sessionName, index) => (
                            <span key={index} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
                              {sessionName}
                            </span>
                          ))}
                          {campaign.sessionNames.length > 3 && (
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-500">
                              +{campaign.sessionNames.length - 3}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-slate-400">Sem sessões</span>
                      )}
                    </td>
                    <td className="px-6 py-5 align-top">
                      <div className="flex justify-end gap-2">
                        <ActionButton label="Ver relatório da campanha" onClick={() => onView(campaign.id)} icon={<EyeIcon />} tone="info" />
                        {campaign.status === 'RUNNING' && (
                          <ActionButton label="Pausar campanha" onClick={() => onPause(campaign.id)} icon={<PauseIcon />} tone="warning" />
                        )}
                        {campaign.status === 'PAUSED' && (
                          <ActionButton label="Retomar campanha" onClick={() => onResume(campaign.id)} icon={<PlayIcon />} tone="success" />
                        )}
                        {['PENDING', 'COMPLETED', 'FAILED'].includes(campaign.status) && (
                          <ActionButton label="Excluir campanha" onClick={() => onDelete(campaign.id)} icon={<TrashIcon />} tone="danger" />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="space-y-4 xl:hidden">
        {campaigns.map((campaign) => {
          const channels = campaign.channels?.length
            ? campaign.channels
            : (campaign.sessionNames?.length ? ['WHATSAPP'] : ['EMAIL']);
          const totalDeliveries = campaign._count?.messages ?? campaign.totalContacts;

          return (
            <article key={campaign.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="truncate text-base font-semibold text-slate-900">{campaign.nome}</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {channels.length === 2
                      ? 'Por Email e WhatsApp'
                      : channels[0] === 'EMAIL'
                        ? 'Por Email'
                        : 'Por WhatsApp'}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <StatusBadge status={campaign.status} />
                    {channels.map((channel) => (
                      <span
                        key={channel}
                        className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          channel === 'EMAIL'
                            ? 'bg-violet-50 text-violet-700 ring-1 ring-violet-200'
                            : 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                        }`}
                      >
                        {channel === 'EMAIL' ? 'Email' : 'WhatsApp'}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <ActionButton label="Ver relatório da campanha" onClick={() => onView(campaign.id)} icon={<EyeIcon />} tone="info" />
                  {campaign.status === 'RUNNING' && (
                    <ActionButton label="Pausar campanha" onClick={() => onPause(campaign.id)} icon={<PauseIcon />} tone="warning" />
                  )}
                  {campaign.status === 'PAUSED' && (
                    <ActionButton label="Retomar campanha" onClick={() => onResume(campaign.id)} icon={<PlayIcon />} tone="success" />
                  )}
                  {['PENDING', 'COMPLETED', 'FAILED'].includes(campaign.status) && (
                    <ActionButton label="Excluir campanha" onClick={() => onDelete(campaign.id)} icon={<TrashIcon />} tone="danger" />
                  )}
                </div>
              </div>

              <dl className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Contatos</dt>
                  <dd className="mt-1 text-sm font-semibold text-slate-900">{campaign.totalContacts}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Enviados</dt>
                  <dd className="mt-1 text-sm font-semibold text-slate-900">{campaign.sentCount}/{totalDeliveries}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Sessões</dt>
                  <dd className="mt-2 flex flex-wrap gap-2">
                    {campaign.sessionNames?.length > 0 ? campaign.sessionNames.slice(0, 3).map((sessionName, index) => (
                      <span key={index} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
                        {sessionName}
                      </span>
                    )) : (
                      <span className="text-sm text-slate-400">Sem sessões</span>
                    )}
                  </dd>
                </div>
              </dl>
            </article>
          );
        })}
      </div>
    </>
  );
}
